import { User, AppRole, Permission } from '../../types/auth';

class RoleService {
  // Define role-to-permissions mapping based on the requirements table. This is the bundled
  // fallback — the source of truth when the API hydration hasn't run yet or has failed.
  private fallbackRolePermissions: Record<AppRole, Permission[]> = {
    // Regional Supervisors (highest level permissions - no request creation)
    [AppRole.SUPERVISOR_NAM]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.CLOSE_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],
    [AppRole.SUPERVISOR_EMEA]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.CLOSE_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],
    [AppRole.SUPERVISOR_APAC]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.CLOSE_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],

    // Default Sponsors (Regional) - Same permissions as Vertiv Sponsor
    [AppRole.DEFAULT_SPONSOR_NAM]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],
    [AppRole.DEFAULT_SPONSOR_EMEA]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],
    [AppRole.DEFAULT_SPONSOR_APAC]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],

    // Standard Roles - Only Requestors can create requests
    [AppRole.REQUESTOR]: [
      Permission.CREATE_REQUEST,
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
      // Note: Requestors can manage Sponsors but NOT Contributors
    ],
    [AppRole.EXTERNAL_REQUESTOR]: [
      Permission.CREATE_REQUEST,
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
    ],
    [AppRole.EXTERNAL_REQUESTOR_LVO]: [
      Permission.CREATE_REQUEST,
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
    ],
    [AppRole.VERTIV_CONTRIBUTOR]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
    ],
    [AppRole.PARTNER_CONTRIBUTOR]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
    ],
    [AppRole.PARTNER_CONTRIBUTOR_LVO]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
    ],
    [AppRole.CONFIG_ENGINEER]: [
      Permission.VIEW_REQUEST,
      Permission.CLOSE_REQUEST,
    ],
    [AppRole.VERTIV_SPONSOR]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
    ],
    [AppRole.IT_ADMINISTRATOR]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
      Permission.CLOSE_REQUEST,
      Permission.MANAGE_SPONSORS,
      Permission.MANAGE_CONTRIBUTORS,
      Permission.MANAGE_ROLES,
    ],
    [AppRole.BUSINESS_OWNER]: [
      Permission.VIEW_REQUEST,
      Permission.EDIT_REQUEST,
    ],
    [AppRole.ROLE_MANAGER]: [Permission.VIEW_REQUEST, Permission.MANAGE_ROLES],

    // Impersonation Roles (permissions are inherited from the target user during impersonation)
    [AppRole.IMPERSONATOR_FULL_ACCESS]: [],
    [AppRole.IMPERSONATOR_READ_ONLY]: [],
  };

  // Set by hydrateRolePermissions() after a successful API fetch (login) or cache hit (offline
  // fallback); null means "not hydrated yet / reset" — activeRolePermissions falls back to the
  // bundled map below. Already contains fallback entries merged in for any role the API response
  // didn't cover, so activeRolePermissions never needs to consult both maps at read time.
  private hydratedRolePermissions: Record<AppRole, Permission[]> | null = null;

  private get activeRolePermissions(): Record<AppRole, Permission[]> {
    return this.hydratedRolePermissions ?? this.fallbackRolePermissions;
  }

  /**
   * Validates the API's role->permissionKey[] map against the AppRole/Permission enums and
   * merges it OVER fallbackRolePermissions — a role absent from the API response keeps its
   * bundled permissions untouched (protects isAuthorizedUser(), which derives its authorized-
   * role set from Object.keys(activeRolePermissions) — silently losing a role key would
   * silently deny that role). Invalid top-level role keys are skipped entirely; invalid
   * permission values inside an otherwise-valid role's array are filtered out individually (the
   * rest of that role's valid permissions are kept, not discarded wholesale). A role key whose
   * value isn't actually an array (a malformed payload) is skipped the same way an unknown role
   * key is — this must never throw, since the caller's fallback chain treats any thrown error
   * identically to a network failure, which would silently misclassify a backend/schema bug as
   * "offline" with no distinguishing signal anywhere.
   *
   * Always merges over fallbackRolePermissions specifically (not over the currently-active map)
   * — repeated calls do not compound; each call is a fresh, complete hydration from the map it's
   * given, not an incremental patch. This matches how it's actually invoked today (at most one
   * hydrate per login/logout cycle, from either a fetch success or a cache fallback, never both).
   */
  hydrateRolePermissions(map: Record<string, string[]>): void {
    const validRoles = new Set<string>(Object.values(AppRole));
    const validPermissions = new Set<string>(Object.values(Permission));
    const merged = { ...this.fallbackRolePermissions } as Record<
      AppRole,
      Permission[]
    >;

    for (const [roleKey, permissionKeys] of Object.entries(map)) {
      if (!validRoles.has(roleKey) || !Array.isArray(permissionKeys)) {
        continue;
      }
      const validated = permissionKeys.filter(p =>
        validPermissions.has(p),
      ) as Permission[];
      merged[roleKey as AppRole] = validated;
    }

    this.hydratedRolePermissions = merged;
  }

  /** Reverts to bundled defaults — called when the app's auth state transitions to logged-out. */
  resetRolePermissions(): void {
    this.hydratedRolePermissions = null;
  }

  /**
   * Check if user has a specific role
   */
  hasRole(user: User | null, role: AppRole): boolean {
    if (!user) return false;
    return user.roles.includes(role);
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(user: User | null, roles: AppRole[]): boolean {
    if (!user) return false;
    return roles.some(role => user.roles.includes(role));
  }

  /**
   * Check if user has all of the specified roles
   */
  hasAllRoles(user: User | null, roles: AppRole[]): boolean {
    if (!user) return false;
    return roles.every(role => user.roles.includes(role));
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(user: User | null, permission: Permission): boolean {
    if (!user) return false;

    return user.roles.some(role => {
      const permissions = this.activeRolePermissions[role];
      return permissions?.includes(permission);
    });
  }

  /**
   * Get all permissions for a user based on their roles
   */
  getUserPermissions(user: User | null): Permission[] {
    if (!user) return [];

    const permissions = new Set<Permission>();
    user.roles.forEach(role => {
      const rolePermissions = this.activeRolePermissions[role];
      rolePermissions?.forEach(permission => permissions.add(permission));
    });

    return Array.from(permissions);
  }

  /**
   * Check if user is authorized to access the application
   * (has at least one valid application role)
   */
  isAuthorizedUser(user: User | null): boolean {
    if (!user) return false;

    // User must have at least one defined role to be authorized
    const authorizedRoles = Object.keys(
      this.activeRolePermissions,
    ) as AppRole[];
    return this.hasAnyRole(user, authorizedRoles);
  }

  /**
   * Check if user is an internal user
   */
  isInternalUser(user: User | null): boolean {
    return user?.userType === 'internal';
  }

  /**
   * Check if user has one of the four internal reviewer roles
   * (Vertiv Sponsor, Vertiv Contributor, Config Engineer, Business Owner).
   * Used to default these roles into the Advanced field view at post-DRAFT
   * stages on RequestDetail (Story 4.1 / Bug 8 / FR9).
   */
  isInternalReviewerRole(user: User | null): boolean {
    return this.hasAnyRole(user, [
      AppRole.VERTIV_SPONSOR,
      AppRole.VERTIV_CONTRIBUTOR,
      AppRole.CONFIG_ENGINEER,
      AppRole.BUSINESS_OWNER,
    ]);
  }

  /**
   * Check if user is an external user
   */
  isExternalUser(user: User | null): boolean {
    return user?.userType === 'external';
  }

  /**
   * Get the highest privilege role for the user based on permission count
   */
  getHighestRole(user: User | null): AppRole | null {
    if (!user || user.roles.length === 0) return null;

    // Define role hierarchy (highest to lowest privilege based on permissions)
    const roleHierarchy = [
      // Regional Supervisors (highest privileges)
      AppRole.SUPERVISOR_NAM, // Full regional supervisory capabilities
      AppRole.SUPERVISOR_EMEA, // Full regional supervisory capabilities
      AppRole.SUPERVISOR_APAC, // Full regional supervisory capabilities

      // Senior Management Roles
      AppRole.REQUESTOR, // Full management capabilities
      AppRole.VERTIV_SPONSOR, // Full management capabilities

      // Standard User Roles
      AppRole.BUSINESS_OWNER, // Edit and view
      AppRole.VERTIV_CONTRIBUTOR, // Edit and view
      AppRole.PARTNER_CONTRIBUTOR, // Edit and view
      AppRole.PARTNER_CONTRIBUTOR_LVO,
      AppRole.EXTERNAL_REQUESTOR, // Create, edit and view
      AppRole.EXTERNAL_REQUESTOR_LVO,
      AppRole.CONFIG_ENGINEER, // View and close
      AppRole.IT_ADMINISTRATOR, // System administration
      AppRole.ROLE_MANAGER, // Role management only
    ];

    for (const role of roleHierarchy) {
      if (user.roles.includes(role)) {
        return role;
      }
    }

    return user.roles[0]; // Return first role if none match hierarchy
  }

  /**
   * Determine which component/screen to show after authentication
   */
  getPostAuthRoute(user: User | null): string {
    if (!user) return '/login';

    // Check if user is authorized (has valid ITS role)
    if (!this.isAuthorizedUser(user)) {
      // Root — App renders Login with access denial (ITS_TC_101); no separate screen.
      return '/';
    }

    // Both internal and external authorized users go to requests page
    return '/request';
  }

  /**
   * Convenience methods for checking specific permissions
   */
  canCreateRequest(user: User | null): boolean {
    return this.hasPermission(user, Permission.CREATE_REQUEST);
  }

  canEditRequest(user: User | null): boolean {
    return this.hasPermission(user, Permission.EDIT_REQUEST);
  }

  canManageSponsors(user: User | null): boolean {
    return this.hasPermission(user, Permission.MANAGE_SPONSORS);
  }

  canManageContributors(user: User | null): boolean {
    return this.hasPermission(user, Permission.MANAGE_CONTRIBUTORS);
  }

  canViewRequest(user: User | null): boolean {
    return this.hasPermission(user, Permission.VIEW_REQUEST);
  }

  canCloseRequest(user: User | null): boolean {
    return this.hasPermission(user, Permission.CLOSE_REQUEST);
  }

  /**
   * Check if user can perform administrative actions (full management)
   * Regional Supervisors and Vertiv Sponsors have full management capabilities
   * Requestors can manage sponsors but not contributors
   */
  isAdmin(user: User | null): boolean {
    return this.hasAnyRole(user, [
      AppRole.SUPERVISOR_NAM,
      AppRole.SUPERVISOR_EMEA,
      AppRole.SUPERVISOR_APAC,
      AppRole.VERTIV_SPONSOR,
      AppRole.DEFAULT_SPONSOR_NAM,
      AppRole.DEFAULT_SPONSOR_EMEA,
      AppRole.DEFAULT_SPONSOR_APAC,
    ]);
  }

  /**
   * Get display-friendly role name
   */
  getRoleDisplayName(role: AppRole): string {
    const displayNames: Record<AppRole, string> = {
      // Regional Supervisors
      [AppRole.SUPERVISOR_NAM]: 'Supervisor NAM',
      [AppRole.SUPERVISOR_EMEA]: 'Supervisor EMEA',
      [AppRole.SUPERVISOR_APAC]: 'Supervisor APAC',

      // Default Sponsors (Regional)
      [AppRole.DEFAULT_SPONSOR_NAM]: 'Default Sponsor NAM',
      [AppRole.DEFAULT_SPONSOR_EMEA]: 'Default Sponsor EMEA',
      [AppRole.DEFAULT_SPONSOR_APAC]: 'Default Sponsor APAC',

      // Standard Roles
      [AppRole.REQUESTOR]: 'Requestor',
      [AppRole.EXTERNAL_REQUESTOR]: 'External Requestor',
      [AppRole.EXTERNAL_REQUESTOR_LVO]: 'External Requestor LVO',
      [AppRole.VERTIV_CONTRIBUTOR]: 'Vertiv Contributor',
      [AppRole.PARTNER_CONTRIBUTOR]: 'Partner Contributor',
      [AppRole.PARTNER_CONTRIBUTOR_LVO]: 'Partner Contributor LVO',
      [AppRole.CONFIG_ENGINEER]: 'Config Engineer',
      [AppRole.VERTIV_SPONSOR]: 'Vertiv Sponsor',
      [AppRole.IT_ADMINISTRATOR]: 'IT Administrator',
      [AppRole.BUSINESS_OWNER]: 'Business Owner',
      [AppRole.ROLE_MANAGER]: 'Role Manager',
      [AppRole.IMPERSONATOR_FULL_ACCESS]: 'Impersonator Full Access',
      [AppRole.IMPERSONATOR_READ_ONLY]: 'Impersonator Read Only',
    };

    return displayNames[role] || role;
  }

  /**
   * Get user type display name
   */
  getUserTypeDisplayName(user: User | null): string {
    if (!user) return 'Unknown';
    return user.userType === 'internal' ? 'Internal User' : 'External User';
  }

  /**
   * Check if user is a Requestor
   */
  isRequestor(user: User | null): boolean {
    return this.hasRole(user, AppRole.REQUESTOR);
  }

  /**
   * Check if user is an External Requestor
   */
  isExternalRequestor(user: User | null): boolean {
    return this.hasRole(user, AppRole.EXTERNAL_REQUESTOR);
  }

  /**
   * Check if user is an IT Administrator
   */
  isITAdministrator(user: User | null): boolean {
    return this.hasRole(user, AppRole.IT_ADMINISTRATOR);
  }

  /**
   * Check if user can manage Default Sponsors (IT Administrator or Role Manager)
   */
  canManageDefaultSponsors(user: User | null): boolean {
    if (!user) return false;
    return (
      this.hasRole(user, AppRole.IT_ADMINISTRATOR) ||
      this.hasRole(user, AppRole.ROLE_MANAGER)
    );
  }

  /**
   * Check if user can access the Users page (formerly Role Management)
   * Includes IT Administrators, Role Managers, and Impersonators
   * Each role sees different features on the page (role columns, Login As, etc.)
   * @temporary Development access - TODO: Remove before production
   */
  canAccessRoleManagement(user: User | null): boolean {
    if (!user) return false;

    // Check if user has any role that uses features on the Users page
    if (
      this.hasRole(user, AppRole.IT_ADMINISTRATOR) ||
      this.hasRole(user, AppRole.ROLE_MANAGER) ||
      this.hasRole(user, AppRole.IMPERSONATOR_FULL_ACCESS) ||
      this.hasRole(user, AppRole.IMPERSONATOR_READ_ONLY)
    ) {
      return true;
    }

    // Development-only bypass via environment variable (should be empty in production)
    const devAdminUpns = process.env.REACT_APP_DEV_ADMIN_UPNS;
    if (devAdminUpns) {
      const allowedUpns = devAdminUpns
        .split(',')
        .map(email => email.trim().toLowerCase());
      if (allowedUpns.includes(user.email.toLowerCase())) {
        console.warn(`[SECURITY] Dev admin bypass used for role management`);
        return true;
      }
    }

    return false;
  }

  /**
   * Check if user is a Role Manager
   */
  isRoleManager(user: User | null): boolean {
    return this.hasRole(user, AppRole.ROLE_MANAGER);
  }

  /**
   * Check if user can access prompt management features
   * Only IT Administrators should have access in production
   */
  canAccessPromptManagement(user: User | null): boolean {
    if (!user) return false;

    // Check if user has IT Administrator role (production path)
    if (this.hasRole(user, AppRole.IT_ADMINISTRATOR)) {
      return true;
    }

    // Development-only bypass via environment variable (should be empty in production)
    const devAdminUpns = process.env.REACT_APP_DEV_ADMIN_UPNS;
    if (devAdminUpns) {
      const allowedUpns = devAdminUpns
        .split(',')
        .map(email => email.trim().toLowerCase());
      if (allowedUpns.includes(user.email.toLowerCase())) {
        console.warn(`[SECURITY] Dev admin bypass used for prompt management`);
        return true;
      }
    }

    return false;
  }

  /**
   * Check if user can access the feedback view feature
   * Only IT Administrators should have access to view aggregated feedback
   */
  canAccessFeedbackView(user: User | null): boolean {
    if (!user) return false;

    // Check if user has IT Administrator role (production path)
    if (this.hasRole(user, AppRole.IT_ADMINISTRATOR)) {
      return true;
    }

    // Development-only bypass via environment variable (should be empty in production)
    const devAdminUpns = process.env.REACT_APP_DEV_ADMIN_UPNS;
    if (devAdminUpns) {
      const allowedUpns = devAdminUpns
        .split(',')
        .map(email => email.trim().toLowerCase());
      if (allowedUpns.includes(user.email.toLowerCase())) {
        console.warn(
          `[SECURITY] Dev admin bypass used for feedback view: ${user.email}`,
        );
        return true;
      }
    }

    return false;
  }
}

// AD Group to Role mapping - includes both legacy email-based groups and real Microsoft Graph group IDs
const AD_GROUP_TO_ROLE_MAP: Record<string, AppRole> = {
  // Legacy email-based groups (for backward compatibility)
  'SG-ITS-Test-Int-End-Users@vertiv.com': AppRole.REQUESTOR,
  'SG-ITS-Test-Ext-End-Users@vertiv.com': AppRole.EXTERNAL_REQUESTOR,
  'SG-ITS-Test-Int-Contributors@vertiv.com': AppRole.VERTIV_CONTRIBUTOR,
  'SG-ITS-Test-Ext-Contributors@vertiv.com': AppRole.PARTNER_CONTRIBUTOR,
  'SG-ITS-Test-Int-Config-Engineers@vertiv.com': AppRole.CONFIG_ENGINEER,
  'SG-ITS-Test-Int-Sponsors@vertiv.com': AppRole.VERTIV_SPONSOR,
  'SG-ITS-Test-ADV-Users@vertiv.com': AppRole.IT_ADMINISTRATOR,
  'SG-ITS-Test-Int-BusinessOwners@vertiv.com': AppRole.BUSINESS_OWNER,

  // Real Microsoft Graph group IDs (from production Azure AD)
  '2cc25f51-75bb-41a5-8ed9-109762986fa3': AppRole.IT_ADMINISTRATOR, // Reader group
  'faff0197-b08a-4257-a5c9-7d9a025a5e85': AppRole.IT_ADMINISTRATOR, // Admin group

  // Production Entra group IDs (provided by client)
  '6973a9c7-081c-4ee7-86a2-149e990f8fb0': AppRole.SUPERVISOR_NAM,
  'a9fafba6-bfb3-432b-b2e8-85b35b9213b9': AppRole.SUPERVISOR_EMEA,
  'a09ebb11-6f9d-42c6-88e5-9b0aa52f7dd0': AppRole.SUPERVISOR_APAC,
  'cad58f3f-a2ef-433a-bc92-7052b5716b0b': AppRole.REQUESTOR,
  'f56c068e-b091-4e3f-8d8d-0c1f784fed87': AppRole.EXTERNAL_REQUESTOR,
  'dc7f8932-63c7-477f-a90b-f6042b293d7f': AppRole.VERTIV_CONTRIBUTOR,
  'e4754780-688c-48e0-a020-cb79217b5512': AppRole.PARTNER_CONTRIBUTOR,
  '1f7830d9-fc92-4491-9d4a-d27e454791a2': AppRole.CONFIG_ENGINEER,
  '36bc8186-7553-42f6-ad27-c179095b04c3': AppRole.VERTIV_SPONSOR,
  '47171bf4-d2e8-453e-8a0e-e7c5d476e2d5': AppRole.IT_ADMINISTRATOR,
  '06f264f9-9101-47c3-b0dd-80a9af04a3c7': AppRole.BUSINESS_OWNER,
};

/**
 * Map AD groups to application roles
 */
export function mapGroupsToRoles(adGroups: string[]): AppRole[] {
  const roles: AppRole[] = [];

  for (const group of adGroups) {
    const role = AD_GROUP_TO_ROLE_MAP[group];
    if (role && !roles.includes(role)) {
      roles.push(role);
    }
  }

  return roles;
}

// Export singleton instance
export const roleService = new RoleService();
